import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon, SettingsIcon } from 'lucide-react';
import type { RunMode } from '../../types/aus';
import { Toggle } from './Toggle';
interface AdvancedOptionsProps {
  mode: RunMode;
  skipReissue: boolean;
  onSkipReissueChange: (value: boolean) => void;
  isResubmission: boolean;
  onResubmissionChange: (value: boolean) => void;
  lpaKeyNumber: string;
  onLpaKeyNumberChange: (value: string) => void;
  onEditReference: () => void;
  onManageCredentials: () => void;
}
export function AdvancedOptions({
  mode,
  skipReissue,
  onSkipReissueChange,
  isResubmission,
  onResubmissionChange,
  lpaKeyNumber,
  onLpaKeyNumberChange,
  onEditReference,
  onManageCredentials
}: AdvancedOptionsProps) {
  const [open, setOpen] = useState(false);
  const showFannieOptions = mode !== 'freddie';
  const showFreddieOptions = mode !== 'fannie';
  const keyInvalid = isResubmission && lpaKeyNumber.trim().length > 0 && lpaKeyNumber.trim().length < 6;
  return <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" data-id="element-17" data-mp="3fumi285xgxbp">
      <button type="button" aria-expanded={open} onClick={() => setOpen(value => !value)} className="flex w-full items-center justify-between gap-4 px-5 py-3.5 text-left transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-900" data-id="element-18" data-mp="3ksnzyprmaqll">
        <span className="flex items-center gap-2 text-sm font-medium text-slate-700" data-id="element-19" data-mp="31ubp8zl258nw">
          <SettingsIcon aria-hidden="true" className="h-4 w-4 text-slate-400" data-id="element-20" data-mp="31w4zeouvqb0n" />
          Advanced options
        </span>
        <ChevronDownIcon aria-hidden="true" className={`h-4 w-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-21" data-mp="2s48wn0hulwq3" />
      </button>

      <AnimatePresence initial={false} data-id="element-22" data-mp="2e73kcirjk1me">
        {open && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: 'easeOut'
      }} data-id="element-23" data-mp="2i51bvs8sazsy">
            <div className="space-y-4 border-t border-slate-100 px-5 py-4" data-id="element-24" data-mp="jv2jc44fczdz">
              {showFannieOptions && <Toggle checked={skipReissue} onChange={onSkipReissueChange} label="Continue without credit reissue information" description="Submits without reissuing the existing credit report. Rarely needed." data-id="element-25" data-mp="9yzgew1r0wpz" />}

              {showFreddieOptions && <div className={showFannieOptions ? 'border-t border-slate-100 pt-4' : ''} data-id="element-26" data-mp="xmh1y9uf6yki">
                  <Toggle checked={isResubmission} onChange={value => {
              onResubmissionChange(value);
              if (!value) onLpaKeyNumberChange('');
            }} label="This is an LPA resubmission" description="Only needed when resubmitting a loan previously assessed by Loan Product Advisor." data-id="element-27" data-mp="tmf8gnyu9bgb" />
                  {isResubmission && <div className="mt-3" data-id="element-28" data-mp="22urxmwquzwbn">
                      <label htmlFor="lpa-key" className="block text-xs font-semibold uppercase tracking-wide text-slate-400" data-id="element-29" data-mp="22tqjukigkvq8">
                        LPA Key Number
                      </label>
                      <input id="lpa-key" type="text" value={lpaKeyNumber} onChange={event => onLpaKeyNumberChange(event.target.value)} aria-invalid={keyInvalid} aria-describedby={keyInvalid ? 'lpa-key-error' : undefined} placeholder="e.g. A20-4471928" className={`mt-1.5 w-full max-w-xs rounded-xl border px-3 py-2 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:outline-none focus-visible:ring-2 ${keyInvalid ? 'border-amber-300 focus-visible:ring-amber-500' : 'border-slate-200 focus-visible:ring-brand-700'}`} data-id="element-30" data-mp="2hiwhi41nedw0" />
                      {keyInvalid && <p id="lpa-key-error" className="mt-1.5 text-xs font-medium text-amber-700" data-id="element-31" data-mp="3eccoyoti2zyk">
                          Enter the full key number from the previous LPA submission.
                        </p>}
                    </div>}
                </div>}

              <div className="flex flex-wrap gap-x-5 gap-y-2 border-t border-slate-100 pt-3.5" data-id="element-32" data-mp="3j8uqzgxg4k77">
                <button type="button" onClick={onEditReference} className="text-sm font-medium text-slate-600 underline-offset-2 hover:text-slate-900 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900" data-id="element-33" data-mp="2vj8sywst8b0s">
                  View or edit credit reference
                </button>
                <button type="button" onClick={onManageCredentials} className="text-sm font-medium text-slate-600 underline-offset-2 hover:text-slate-900 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900" data-id="element-34" data-mp="w7mpvyv88kbf">
                  Credential details
                </button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}